import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { PATHS } from 'router/config';

import * as S from '../../FakturowniaInvoices/FakturowniaInvoices.styled';
import * as L from '../StudentDetail.styled';
import { IStudent } from './types';

interface IGroupsCardProps {
  student: IStudent;
}

interface IGroupRow {
  id: string;
  name: string;
  courseName?: string;
  active: boolean;
}

/** «Какие группы посещает» — derived from active+terminated agreements. */
export const GroupsCard = memo(({ student }: IGroupsCardProps) => {
  const { t } = useTranslation();
  const history = useHistory();

  // De-dupe by group id — student can have several agreements in the same
  // group (resumed contract), and showing the row twice would confuse.
  const groups: IGroupRow[] = useMemo(() => {
    const seen = new Map<string, IGroupRow>();
    (student.agreements || []).forEach((a) => {
      if (!a?.group) return;
      const prev = seen.get(a.group.id);
      const active = !a.terminated;
      if (!prev || (active && !prev.active)) {
        seen.set(a.group.id, {
          id: a.group.id,
          name: a.group.name,
          courseName: a.group.course?.name,
          active,
        });
      }
    });
    return Array.from(seen.values()).sort((x, y) =>
      x.active === y.active ? 0 : x.active ? -1 : 1,
    );
  }, [student.agreements]);

  return (
    <S.Card>
      <S.CardTitle>
        {t('pages.studentDetail.overview.groupsSection')}
      </S.CardTitle>
      {groups.length === 0 ? (
        <S.SubTitle>{t('pages.studentDetail.overview.noGroups')}</S.SubTitle>
      ) : (
        groups.map((g) => (
          <S.Row key={g.id}>
            <S.RowLabel>
              {g.active ? (
                <L.Tag tone="ok">
                  {t('pages.studentDetail.overview.active')}
                </L.Tag>
              ) : (
                <L.Tag tone="neutral">
                  {t('pages.studentDetail.overview.terminated')}
                </L.Tag>
              )}
            </S.RowLabel>
            <S.RowValue>
              <div>
                <S.RawPayloadToggle
                  onClick={() =>
                    history.push(PATHS.groupDetail.replace(':id', g.id))
                  }
                >
                  {g.name}
                </S.RawPayloadToggle>
              </div>
              {g.courseName ? <L.Muted>{g.courseName}</L.Muted> : null}
            </S.RowValue>
          </S.Row>
        ))
      )}
    </S.Card>
  );
});
